/**
 * Custom Image Node for Lexical
 * A void/decorator node for images with blockId and metadata support
 */

import type { InlineContent } from '@shared/ast/astTypes'
import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMExportOutput,
  DecoratorNode,
  EditorConfig,
  LexicalNode,
  SerializedLexicalNode,
  Spread,
} from 'lexical'

import { resolveImageUrl } from '../../../../utils/imageUrl'

export type SerializedCustomImageNode = Spread<
  {
    blockId: string
    url: string
    alt?: string
    width?: number
    height?: number
    caption?: InlineContent[]
    align?: 'left' | 'right' | 'center'
  },
  SerializedLexicalNode
>

export class CustomImageNode extends DecoratorNode<JSX.Element> {
  __blockId: string
  __url: string
  __alt?: string
  __width?: number
  __height?: number
  __caption?: InlineContent[]
  __align?: 'left' | 'right' | 'center'

  static getType(): string {
    return 'custom-image'
  }

  static clone(node: CustomImageNode): CustomImageNode {
    return new CustomImageNode(
      node.__blockId,
      node.__url,
      node.__alt,
      node.__width,
      node.__height,
      node.__caption,
      node.__align,
      node.__key,
    )
  }

  constructor(
    blockId: string,
    url: string,
    alt?: string,
    width?: number,
    height?: number,
    caption?: InlineContent[],
    align?: 'left' | 'right' | 'center',
    key?: string,
  ) {
    super(key)
    this.__blockId = blockId
    this.__url = url
    this.__alt = alt
    this.__width = width
    this.__height = height
    this.__caption = caption
    this.__align = align
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getUrl(): string {
    return this.__url
  }

  setUrl(url: string): this {
    const writable = this.getWritable()
    writable.__url = url
    return writable
  }

  getAlt(): string | undefined {
    return this.__alt
  }

  setAlt(alt: string | undefined): this {
    const writable = this.getWritable()
    writable.__alt = alt
    return writable
  }

  getWidth(): number | undefined {
    return this.__width
  }

  setWidth(width: number | undefined): this {
    const writable = this.getWritable()
    writable.__width = width
    return writable
  }

  getHeight(): number | undefined {
    return this.__height
  }

  setHeight(height: number | undefined): this {
    const writable = this.getWritable()
    writable.__height = height
    return writable
  }

  getCaption(): InlineContent[] | undefined {
    return this.__caption
  }

  setCaption(caption: InlineContent[] | undefined): this {
    const writable = this.getWritable()
    writable.__caption = caption
    return writable
  }

  getAlign(): 'left' | 'right' | 'center' | undefined {
    return this.__align
  }

  setAlign(align: 'left' | 'right' | 'center' | undefined): this {
    const writable = this.getWritable()
    writable.__align = align
    return writable
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement('div')
    div.setAttribute('data-block-id', this.__blockId)
    div.style.margin = '32px 0'
    div.style.textAlign = this.__align || 'center'
    div.setAttribute('contenteditable', 'false')

    // Create the actual image element — soft edges for watercolor illustration feel
    const img = document.createElement('img')
    img.src = resolveImageUrl(this.__url)
    img.alt = this.__alt || ''
    img.style.maxWidth = '85%'
    img.style.height = 'auto'
    img.style.borderRadius = '0'
    if (this.__width) {
      img.width = this.__width
    }
    if (this.__height) {
      img.height = this.__height
    }
    div.appendChild(img)

    return div
  }

  updateDOM(prevNode: CustomImageNode, dom: HTMLElement): boolean {
    if (prevNode.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevNode.__align !== this.__align) {
      dom.style.textAlign = this.__align || 'center'
    }
    // Update image src if changed
    if (prevNode.__url !== this.__url) {
      const img = dom.querySelector('img')
      if (img) {
        img.src = resolveImageUrl(this.__url)
      }
    }
    if (prevNode.__alt !== this.__alt) {
      const img = dom.querySelector('img')
      if (img) {
        img.alt = this.__alt || ''
      }
    }
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: (element: HTMLElement) => {
          const imgElement = element as HTMLImageElement
          const blockId = element.getAttribute('data-block-id') || ''
          const url = imgElement.src || ''
          const alt = imgElement.alt || undefined
          const width = imgElement.width || undefined
          const height = imgElement.height || undefined
          return {
            node: $createCustomImageNode(blockId, url, alt, width, height),
          }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const img = document.createElement('img')
    img.setAttribute('data-block-id', this.__blockId)
    img.src = this.__url
    if (this.__alt) {
      img.alt = this.__alt
    }
    if (this.__width) {
      img.width = this.__width
    }
    if (this.__height) {
      img.height = this.__height
    }
    return { element: img }
  }

  static importJSON(serializedNode: SerializedCustomImageNode): CustomImageNode {
    return $createCustomImageNode(
      serializedNode.blockId,
      serializedNode.url,
      serializedNode.alt,
      serializedNode.width,
      serializedNode.height,
      serializedNode.caption,
      serializedNode.align,
    )
  }

  exportJSON(): SerializedCustomImageNode {
    return {
      type: 'custom-image',
      version: 1,
      blockId: this.__blockId,
      url: this.__url,
      alt: this.__alt,
      width: this.__width,
      height: this.__height,
      caption: this.__caption,
      align: this.__align,
    }
  }

  isInline(): boolean {
    return false
  }

  decorate(): JSX.Element {
    // Return null - actual rendering is handled in LexicalEditor component
    return null as unknown as JSX.Element
  }
}

export function $createCustomImageNode(
  blockId: string,
  url: string,
  alt?: string,
  width?: number,
  height?: number,
  caption?: InlineContent[],
  align?: 'left' | 'right' | 'center',
): CustomImageNode {
  return $applyNodeReplacement(new CustomImageNode(blockId, url, alt, width, height, caption, align))
}

export function $isCustomImageNode(node: LexicalNode | null | undefined): node is CustomImageNode {
  return node instanceof CustomImageNode
}
